
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Paperclip, UploadCloud, FileText, Download, Edit2, Loader2 } from "lucide-react";
import { useNotify } from "@/hooks/useNotify";
import { extractStoragePath, getSignedUrl } from "@/hooks/useSignedUrl";

interface ProjectDoc {
  id: string;
  file_url: string;
  file_type: string;
  description: string | null;
  uploaded_at: string;
  original_filename: string | null;
}

const ALLOWED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  // Excel files
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  // Word documents
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  // PowerPoint
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  // Text files
  "text/plain",
  "text/csv",
  // OpenDocument formats
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.oasis.opendocument.presentation",
];

export default function ProjectDocuments({ projectId }: { projectId: string }) {
  const [docs, setDocs] = useState<ProjectDoc[]>([]);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [desc, setDesc] = useState("");
  const [customFileName, setCustomFileName] = useState("");
  const [editingDoc, setEditingDoc] = useState<ProjectDoc | null>(null);
  const [newFilename, setNewFilename] = useState("");
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const notify = useNotify();

  async function fetchDocs() {
    const { data, error } = await supabase
      .from("project_documents")
      .select("*")
      .eq("project_id", projectId)
      .order("uploaded_at", { ascending: false });
    if (data) setDocs(data);
  }

  useEffect(() => {
    if (projectId) fetchDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Reset custom filename when file changes
  useEffect(() => {
    if (file) {
      setCustomFileName(file.name);
    } else {
      setCustomFileName("");
    }
  }, [file]);

  // Generate safe filename with conflict resolution
  const generateSafeFileName = async (originalName: string): Promise<string> => {
    const nameWithoutExt = originalName.substring(0, originalName.lastIndexOf('.')) || originalName;
    const extension = originalName.substring(originalName.lastIndexOf('.')) || '';
    
    // Check if file with this name already exists
    const { data: existingDocs } = await supabase
      .from("project_documents")
      .select("original_filename")
      .eq("project_id", projectId);
    
    const existingNames = existingDocs?.map(doc => doc.original_filename) || [];
    
    let finalName = originalName;
    let counter = 1;
    
    while (existingNames.includes(finalName)) {
      finalName = `${nameWithoutExt} (${counter})${extension}`;
      counter++;
    }
    
    return finalName;
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    
    if (!ALLOWED_TYPES.includes(file.type)) {
      notify.error("Please select a valid PDF, image, Excel, or document file");
      setUploading(false);
      return;
    }

    const finalFileName = await generateSafeFileName(customFileName || file.name);
    const fileExt = file.name.split(".").pop();
    
    // Use timestamp + random for storage filename to avoid storage conflicts
    const storageFileName = `${projectId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;
    
    const { error: uploadError } = await supabase.storage
      .from("project_docs")
      .upload(storageFileName, file);
      
    if (uploadError) {
      notify.error(uploadError);
      setUploading(false);
      return;
    }

    // Store the storage path (not public URL since bucket is private)
    // Get current user's company_id for RLS compliance
    const { data: profile } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', (await supabase.auth.getUser()).data.user?.id)
      .single();

    const { error } = await supabase
      .from("project_documents")
      .insert({
        project_id: projectId,
        file_url: storageFileName, // Store path, not public URL
        file_type: file.type,
        description: desc,
        original_filename: finalFileName,
        uploaded_by: (await supabase.auth.getUser()).data.user?.id,
        company_id: profile?.company_id,
      });
      
    if (!error) {
      notify.success(`File uploaded: ${finalFileName}`);
      setFile(null);
      setDesc("");
      setCustomFileName("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchDocs();
    } else {
      notify.error(error);
    }
    setUploading(false);
  };

  const handleRename = async () => {
    if (!editingDoc || !newFilename.trim()) return;

    const finalFileName = await generateSafeFileName(newFilename.trim());
    
    const { error } = await supabase
      .from("project_documents")
      .update({ original_filename: finalFileName })
      .eq("id", editingDoc.id);

    if (!error) {
      notify.success(`File renamed to: ${finalFileName}`);
      setEditingDoc(null);
      setNewFilename("");
      setIsRenameDialogOpen(false);
      fetchDocs();
    } else {
      notify.error(error);
    }
  };

  const startRename = (doc: ProjectDoc) => {
    setEditingDoc(doc);
    setNewFilename(doc.original_filename || "untitled");
    setIsRenameDialogOpen(true);
  };

  // Handle document download/view with signed URL
  const handleDocumentAccess = async (doc: ProjectDoc, action: 'view' | 'download') => {
    // Extract storage path - handle both old (full URL) and new (path only) formats
    const storagePath = extractStoragePath(doc.file_url, 'project_docs') || doc.file_url;
    
    const signedUrl = await getSignedUrl('project_docs', storagePath);
    
    if (!signedUrl) {
      notify.error("Could not generate access link for this file");
      return;
    }

    if (action === 'download') {
      // Create a temporary anchor to trigger download
      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = doc.original_filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // Open in new tab for viewing
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Paperclip className="h-5 w-5 text-primary" />
          Project Documentation & Files
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4 mb-8">
          <div className="flex gap-2 items-end flex-wrap">
            <Input
              type="file"
              accept={ALLOWED_TYPES.join(",")}
              ref={fileInputRef}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="w-auto"
              disabled={uploading}
            />
            <Input
              type="text"
              placeholder="Add description"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              className="w-48"
              disabled={uploading}
            />
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="gap-2"
            >
              <UploadCloud className="h-4 w-4" />
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </div>
          {file && (
            <div className="flex gap-2 items-center">
              <span className="text-sm text-muted-foreground">Save as:</span>
              <Input
                type="text"
                value={customFileName}
                onChange={(e) => setCustomFileName(e.target.value)}
                className="flex-1"
                disabled={uploading}
                placeholder="Enter filename..."
              />
            </div>
          )}
        </div>
        <div className="text-sm text-gray-600 mb-4">
          Supported file types: PDF, Images (JPG, PNG, GIF, WebP, SVG), Excel (XLS, XLSX), Word (DOC, DOCX), PowerPoint (PPT, PPTX), Text files (TXT, CSV), and OpenDocument formats
        </div>
        <div className="space-y-3">
          {docs.length === 0 ? (
            <div className="text-gray-500">No files uploaded for this project yet.</div>
          ) : (
            docs.map((doc) => (
              <div key={doc.id} className="flex items-center gap-3 border rounded-lg p-3 bg-muted">
                <FileText className="h-5 w-5" />
                <div className="flex-1 min-w-0">
                  <button
                    className="font-medium hover:underline break-all block text-left cursor-pointer"
                    onClick={() => handleDocumentAccess(doc, 'view')}
                  >
                    {doc.original_filename || doc.file_url.split("/").pop()?.split("?")[0] || "untitled"}
                  </button>
                  {doc.description && (
                    <div className="text-gray-600 text-sm mt-1">{doc.description}</div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Dialog open={isRenameDialogOpen} onOpenChange={setIsRenameDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => startRename(doc)}
                        title="Rename file"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Rename File</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <label htmlFor="filename" className="text-sm font-medium">
                            Filename
                          </label>
                          <Input
                            id="filename"
                            value={newFilename}
                            onChange={(e) => setNewFilename(e.target.value)}
                            placeholder="Enter new filename..."
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            onClick={() => {
                              setEditingDoc(null);
                              setNewFilename("");
                              setIsRenameDialogOpen(false);
                            }}
                          >
                            Cancel
                          </Button>
                          <Button onClick={handleRename}>
                            Rename
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDocumentAccess(doc, 'download')}
                    title="Download"
                    className="p-2"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
